export interface CrewRole {
  name: string
  role: string
  role_es?: string
  responsibility: string
  responsibility_es?: string
}

export interface RiskAssessment {
  hazard: string
  likelihood: string
  severity: string
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH'
}

export interface StepHazard {
  hazard: string
  controls: string[]
  riskLevel?: 'LOW' | 'MEDIUM' | 'HIGH'
  isNew?: boolean
}

export interface JobStep {
  description: string
  hazards: StepHazard[]
}

export interface PermitItem {
  id: string
  name: string
  name_es?: string
  needed: boolean
  exists: boolean | null // null = not yet set, true = permit exists, false = permit missing
}

// E/C/M = Risk Driver Tags
// E = Environment, C = Capability, M = Motivation
export type ECMDriver = 'E' | 'C' | 'M'

export const ECM_LABELS: Record<ECMDriver, string> = {
  E: 'Environment',
  C: 'Capability',
  M: 'Motivation',
}

// Hierarchy of Controls
export interface HierarchyControls {
  eliminateSubstitute: string
  engineering: string
  adminTraining: string
  ppe: string
}

export interface StepHazardItem {
  id: string
  hazard: string
  source: string
  ecmDriver: ECMDriver
  controls: HierarchyControls
  equipment: string[]
  relevant: boolean
}

export interface StepHazardData {
  description: string
  description_es?: string
  hazards: StepHazardItem[]
  affectedConditions: string[]
}

export interface GeneralHazardItem {
  id: string
  hazard: string
  source: string
  ecmDriver: ECMDriver
  controls: HierarchyControls
  equipment: string[]
  relevant: boolean
}

export interface WorkConditionCache {
  id: string
  text: string
  text_es?: string
  relevant: boolean | null
  category: 'E' | 'C' | 'M' | null
}

export interface MeetingAddition {
  id: string
  type: 'hazard' | 'control' | 'check'
  stepRef: string
  title: string
  reason: string
  source: string
}

export interface PTPData {
  project: string
  location: string
  date: string
  taskTitle: string
  taskTitle_es?: string
  taskDescription: string
  steps: JobStep[]
  // E/C/M Hazard Data
  stepHazardData: StepHazardData[]
  generalHazards: GeneralHazardItem[]
  generalEquipment: string[]
  // Legacy flat arrays kept for creation flow screens
  jobSteps: string[]
  jobSteps_es?: string[]
  taskEquipment: string[]
  taskEquipment_es?: string[]
  permits: PermitItem[]
  environmentConditions: string[]
  // Cache for preserving full work conditions state across navigation
  _workConditionsCache?: WorkConditionCache[]
  crewSize: number
  crewRoles: CrewRole[]
  selectedHazards: string[]
  riskAssessments: RiskAssessment[]
  engineeringControls: string[]
  adminControls: string[]
  ppeControls: string[]
  equipment: string[]
  safetyChecks: string[]
  meetingScore?: number
  meetingAdditions?: MeetingAddition[]
  foremanSignature?: string // base64 data URL of the signature canvas
  quizAcknowledgmentSignature?: string // base64 data URL of the quiz acknowledgment signature
}

export interface ScreenProps {
  data: PTPData
  updateData: (updates: Partial<PTPData>) => void
  onNext: () => void
  onBack: () => void
  onExport?: () => void
}
